import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CreatedPost, SocialNetworksId } from "@/interfaces";
import { isApiError } from "@/services/bedrock/apiError";
import { DesignPackageServices } from "@/services/bedrock/DesignPackageServices";
import { DesignPackageSummary } from "@/services/bedrock/dtos/DesignPackage";
import { CreateRenderGroupRequest } from "@/services/bedrock/dtos/DesignRenderGroup";
import { Review } from "@/services/bedrock/dtos/Review";
import { ReviewServices } from "@/services/bedrock/ReviewServices";
import { designKeys } from "@/services/bedrock/queryKeys";
import { useAvailableDesigns } from "./hooks/useAvailableDesigns";
import { usePostableReviews } from "./hooks/usePostableReviews";
import { useRenderGroup } from "./hooks/useRenderGroup";
import { useDesignCaption } from "./hooks/useDesignCaption";
import { ShareCaption, shareCaptionKey, useShareCaption } from "./hooks/useShareCaption";
import { useStartShare } from "./hooks/useStartShare";
import { pickBestReviews } from "./pickBestReviews";
import { parseShareParams, ShareParams, writeShareParams } from "./shareSearchParams";

export type ShareEntry =
  | { kind: "review"; reviewId: number }
  | { kind: "library"; promptId: number; designPackageId: number }
  | {
      kind: "coach";
      source: "review" | "prompt" | "package";
      playerId: number;
      playerUserId?: number;
      reviewId?: number;
      designPackageId?: number;
      promptId?: number | null;
      autoPickReviews?: boolean;
    };
export type SharePhase = "closed" | "open";
/** One review on a single-review look, or a carousel of the player's best reviews. */
export type ShareMode = "single" | "carousel";

export interface ShareFlowState {
  phase: SharePhase;
  entry: ShareEntry | null;
  playerId: number | null;
  playerUserId: number | null;
  mode: ShareMode;
  designPackageId: number | null;
  /** The single look to come back to from a carousel. */
  singleDesignId: number | null;
  /** The first one is the review the post is about. */
  reviewIds: number[];
  /** null = the player's default theme. */
  themeId: number | null;
  /** Both background fields null = the design's default. */
  backgroundAssetId: number | null;
  /** Set only for the player's own upload. */
  backgroundImageUrl: string | null;
  renderGroupId: number | null;
  /** Set once posted; the dialog then shows its success state. */
  posted: boolean;
  postedPostId: number | null;
}

export type BackgroundChoice = { assetId: number } | { url: string } | null;

export interface ShareFlowApi {
  open: (entry: ShareEntry) => void;
  close: () => void;
  selectDesign: (id: number) => void;
  /** Library entries: the review the single post is about. */
  selectLeadReview: (id: number) => void;
  startCarousel: () => void;
  endCarousel: () => void;
  setReviewIds: (ids: number[]) => void;
  addBestReviews: () => void;
  selectTheme: (themeId: number) => void;
  selectBackground: (choice: BackgroundChoice) => void;
  retryRender: () => void;
  onPosted: (post: CreatedPost | null) => void;
  openPost: (postId: number, socialNetworkId?: SocialNetworksId) => void;
  closePost: () => void;
}

export interface ShareFlowValue extends ShareFlowState, ShareFlowApi {
  design: DesignPackageSummary | undefined;
  /** The looks the current mode offers. */
  looks: DesignPackageSummary[];
  /** The carousel looks; the carousel option shows only when some exist and more reviews are ready. */
  carouselLooks: DesignPackageSummary[];
  canCarousel: boolean;
  designsLoading: boolean;
  /** Pages of designs still to load; the look gallery fetches them one at a time while open. */
  designsHasMore: boolean;
  designsLoadingMore: boolean;
  designsLoadMoreFailed: boolean;
  loadMoreDesigns: () => void;
  /** The review the post is about; it stays first and can't be removed. */
  pinnedId: number | null;
  pinnedReview: Review | undefined;
  postableReviews: Review[];
  postableLoading: boolean;
  render: ReturnType<typeof useRenderGroup>;
  /** Review designs only: the caption written from the reviews, ahead of the images. */
  caption: ShareCaption;
  /** An unpublished non-review design (a coach's post): the caption written from the design's request; null otherwise. */
  designCaption: ShareCaption | null;
  /** True while the flow waits on designs, reviews, or a restored render before it can show the post. */
  resolving: boolean;
  /** A render is being requested, before the group exists. */
  renderStarting: boolean;
  /** The request for a render failed without a group to show. */
  renderStartFailed: boolean;
  /** The theme the server picked for the player's first render. */
  defaultThemeId: number | null;
  openedPost: { postId: number; socialNetworkId?: SocialNetworksId } | null;
}

export interface ShareFlowProviderProps {
  playerId: number | null;
  playerUserId?: number | null;
  onBehalfOf?: boolean;
  onPosted?: (post: CreatedPost | null) => void;
  children: React.ReactNode;
}

const ShareFlowContext = React.createContext<ShareFlowValue | undefined>(undefined);

export function useShareFlow(): ShareFlowValue {
  const value = React.useContext(ShareFlowContext);
  if (!value) throw new Error("useShareFlow must be used within a ShareFlowHost");
  return value;
}

/** A carousel starts with this many reviews (the pinned one plus the best others). */
export const CAROUSEL_DEFAULT_REVIEWS = 3;
// Lets quick edits to the carousel's reviews settle before a render starts.
const RENDER_DEBOUNCE_MS = 250;

const TOAST_DESIGN_GONE = "That design isn't available anymore.";
const TOAST_COACH_DESIGN_GONE = "This design is no longer available.";
const TOAST_RENDER_GONE = "We couldn't find that post. Let's build it again.";
const TOAST_REVIEWS_NOT_SHAREABLE = "Some of these reviews can't be shared anymore.";
const TOAST_NO_ACCESS = "You don't have access to this.";
const TOAST_BG_UPLOAD_FAILED = "That photo couldn't be uploaded. Try again.";
const TOAST_BG_UNAVAILABLE = "That background isn't available for this design.";
const TOAST_RENDER_FAILED = "We couldn't build this post.";

function closedState(playerId: number | null, playerUserId: number | null): ShareFlowState {
  return {
    phase: "closed",
    entry: null,
    playerId,
    playerUserId,
    mode: "single",
    designPackageId: null,
    singleDesignId: null,
    reviewIds: [],
    themeId: null,
    backgroundAssetId: null,
    backgroundImageUrl: null,
    renderGroupId: null,
    posted: false,
    postedPostId: null,
  };
}

function pinnedIdOf(entry: ShareEntry | null): number | null {
  if (entry?.kind === "review") return entry.reviewId;
  if (entry?.kind === "coach") return entry.reviewId ?? null;
  return null;
}

/**
 * Entries that arrive with their design, from a prompt or the editor: the look is a setting rather than
 * a grid, and a review design starts on the player's best review.
 */
export function isLibraryEntry(entry: ShareEntry | null | undefined): boolean {
  return entry?.kind === "library" || (entry?.kind === "coach" && entry.source !== "review");
}

function entryDesignId(entry: ShareEntry): number | null {
  if (entry.kind === "library") return entry.designPackageId;
  if (entry.kind === "coach") return entry.designPackageId ?? null;
  return null;
}

const isReviewDesign = (design: DesignPackageSummary | undefined) => design?.contextType === "review";
const isCarouselLook = (design: DesignPackageSummary) => isReviewDesign(design) && design.maxReviews > 1;

/** Single-review looks first (newest first within each), then multi-review looks shown with one review. */
function singleLooksOf(designs: DesignPackageSummary[]): DesignPackageSummary[] {
  const review = designs.filter(d => isReviewDesign(d) && d.minReviews <= 1);
  return [...review.filter(d => d.maxReviews === 1), ...review.filter(d => d.maxReviews !== 1)];
}

function restoredState(params: ShareParams, base: ShareFlowState): ShareFlowState {
  const pinned = pinnedIdOf(params.share);
  return {
    ...base,
    phase: "open",
    entry: params.share,
    mode: params.mode === "carousel" ? "carousel" : "single",
    designPackageId: params.design ?? entryDesignId(params.share),
    reviewIds: params.reviews ?? (pinned != null ? [pinned] : []),
    themeId: params.theme ?? null,
    backgroundAssetId: params.bg ?? null,
    backgroundImageUrl: params.bgUrl ?? null,
    renderGroupId: params.render ?? null,
  };
}

function requestFor(state: ShareFlowState, design: DesignPackageSummary): CreateRenderGroupRequest | null {
  if (state.playerId == null) return null;
  // Only review designs take reviews; the server rejects any for the others.
  const reviewIds = isReviewDesign(design) ? state.reviewIds : [];
  if (
    isReviewDesign(design) &&
    (reviewIds.length < Math.max(1, design.minReviews) || reviewIds.length > design.maxReviews)
  )
    return null;
  return {
    designPackageId: design.id,
    playerId: state.playerId,
    reviewIds,
    themeId: state.themeId ?? undefined,
    backgroundAssetId: state.backgroundAssetId ?? undefined,
    backgroundImageUrl: state.backgroundImageUrl ?? undefined,
  };
}

function requestKey(req: CreateRenderGroupRequest | null): string {
  if (!req) return "";
  return [
    req.designPackageId,
    req.playerId,
    req.reviewIds.join(","),
    req.themeId ?? "",
    req.backgroundAssetId ?? "",
    req.backgroundImageUrl ?? "",
  ].join("|");
}

function sameSelection(a: ShareFlowState, b: ShareFlowState): boolean {
  return (
    a.mode === b.mode &&
    a.designPackageId === b.designPackageId &&
    a.reviewIds.join(",") === b.reviewIds.join(",") &&
    a.themeId === b.themeId &&
    a.backgroundAssetId === b.backgroundAssetId &&
    a.backgroundImageUrl === b.backgroundImageUrl
  );
}

function urlParamsFor(state: ShareFlowState): Partial<ShareParams> {
  const rendered = state.renderGroupId != null;
  return {
    share: state.entry ?? undefined,
    // A render id alone restores the rest, so design, reviews, and an upload's URL only stay until it exists.
    design: rendered ? undefined : state.designPackageId ?? undefined,
    reviews: rendered || state.reviewIds.length === 0 ? undefined : state.reviewIds,
    theme: state.themeId ?? undefined,
    bg: state.backgroundAssetId ?? undefined,
    bgUrl: rendered ? undefined : state.backgroundImageUrl ?? undefined,
    render: state.renderGroupId ?? undefined,
    mode: state.mode === "carousel" ? "carousel" : undefined,
  };
}

export function ShareFlowProvider({
  playerId: initialPlayerId,
  playerUserId: initialPlayerUserId = null,
  onPosted: onPostedProp,
  children,
}: ShareFlowProviderProps) {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Restoring is decided before the first render so the URL sync never wipes params it hasn't read.
  const [initialParams] = React.useState(() => parseShareParams(location.search));
  const [state, setState] = React.useState<ShareFlowState>(() => {
    const base = closedState(initialPlayerId, initialPlayerUserId);
    return initialParams ? restoredState(initialParams, base) : base;
  });
  // Waiting on designs (and a restored render) before the post can be shown.
  const [resolving, setResolving] = React.useState(() => !!initialParams);
  const [renderStartFailed, setRenderStartFailed] = React.useState(false);
  const [defaultThemeId, setDefaultThemeId] = React.useState<number | null>(null);
  const [openedPost, setOpenedPost] = React.useState<ShareFlowValue["openedPost"]>(null);
  const [retryNonce, setRetryNonce] = React.useState(0);

  const stateRef = React.useRef(state);
  stateRef.current = state;
  const restoringRender = React.useRef(initialParams?.render != null);
  // The selection the current (or in-flight) render was requested for.
  const lastRequestKey = React.useRef<string>("");

  const isOpen = state.phase !== "closed";
  const pinnedId = pinnedIdOf(state.entry);

  const designsQuery = useAvailableDesigns(isOpen ? state.playerId : null);
  const render = useRenderGroup(state.renderGroupId);
  // Looks a render came back not_found for (unpublished meanwhile); the cached list may still have them.
  const [goneDesignIds, setGoneDesignIds] = React.useState<number[]>([]);

  // The design the flow is about may be missing from the loaded pages: a coach's unpublished design
  // (never listed), or a published one further down the catalog than has loaded (a restored URL, a
  // render being restored). One detail fetch brings it in.
  const coachDesignId = state.entry?.kind === "coach" ? entryDesignId(state.entry) : null;
  const listed = designsQuery.data ?? [];
  const coachDesignListed = coachDesignId != null && listed.some(d => d.id === coachDesignId);
  const wantedDesignId = state.designPackageId ?? render.group?.designPackageId ?? coachDesignId ?? null;
  const missingDesignId = wantedDesignId != null && !listed.some(d => d.id === wantedDesignId) ? wantedDesignId : null;
  const unlistedDesign = useQuery({
    queryKey: designKeys.detail(missingDesignId ?? 0),
    queryFn: () => DesignPackageServices.get(missingDesignId as number),
    enabled: isOpen && missingDesignId != null && designsQuery.isFetched,
    staleTime: 15_000,
    retry: false,
  });
  // Only what the available list would have held: a coach's own design, never a global template
  const fetched = unlistedDesign.data;
  const unlistedDesignData =
    missingDesignId != null &&
    fetched &&
    !fetched.isGlobal &&
    (fetched.publishedAt || missingDesignId === coachDesignId)
      ? fetched
      : undefined;
  const unlistedPending =
    missingDesignId != null && designsQuery.isFetched && !unlistedDesign.data && !unlistedDesign.isError;

  const allDesigns = React.useMemo(
    () =>
      [...(designsQuery.data ?? []), ...(unlistedDesignData ? [unlistedDesignData] : [])].filter(
        d => !goneDesignIds.includes(d.id)
      ),
    [designsQuery.data, unlistedDesignData, goneDesignIds]
  );
  const designGoneToast = state.entry?.kind === "coach" ? TOAST_COACH_DESIGN_GONE : TOAST_DESIGN_GONE;
  const design = allDesigns.find(d => d.id === state.designPackageId);
  const designById = (id: number | null) => allDesigns.find(d => d.id === id);

  const postable = usePostableReviews(state.playerId, isOpen);
  const postableReviews = React.useMemo(() => postable.data ?? [], [postable.data]);

  const pinnedInList = pinnedId != null && postableReviews.some(r => Number(r.id) === pinnedId);
  // Shares the review panel's cache entry, which carries more fields; never refetch over it.
  const pinnedDetails = useQuery({
    queryKey: ["review-details", String(pinnedId)],
    queryFn: () => ReviewServices.getReview(pinnedId as number, {}),
    enabled: isOpen && pinnedId != null && !pinnedInList && !postable.isLoading,
    staleTime: Infinity,
  });
  const pinnedReview = pinnedInList
    ? postableReviews.find(r => Number(r.id) === pinnedId)
    : (pinnedDetails.data as Review | undefined) ?? undefined;

  // An unpublished design is the only look this post can use.
  const fixedLook = !!design && design.id === unlistedDesignData?.id && !design.publishedAt;
  const looks = React.useMemo(() => {
    if (fixedLook || (isLibraryEntry(state.entry) && !isReviewDesign(design))) return design ? [design] : [];
    return state.mode === "carousel" ? allDesigns.filter(isCarouselLook) : singleLooksOf(allDesigns);
  }, [allDesigns, state.mode, state.entry, design, fixedLook]);
  const carouselLooks = React.useMemo(
    () => (fixedLook ? [design!].filter(isCarouselLook) : allDesigns.filter(isCarouselLook)),
    [allDesigns, fixedLook, design]
  );
  const leadReviewId = state.reviewIds[0] ?? null;
  const otherReviewsReady = postableReviews.some(r => Number(r.id) !== leadReviewId);
  const canCarousel = isReviewDesign(design) && carouselLooks.length > 0 && otherReviewsReady;

  const startShare = useStartShare();
  // Stable, so the gallery's load-more effect doesn't restart an in-flight page on every render
  const { fetchNextPage: fetchNextDesignsPage } = designsQuery;
  const loadMoreDesigns = React.useCallback(() => void fetchNextDesignsPage(), [fetchNextDesignsPage]);

  // The caption depends on the reviews, not the look, so it starts as soon as they're known.
  // While a render is being restored its own caption comes with it, so nothing is written yet.
  const captionWanted =
    isOpen &&
    !state.posted &&
    !(resolving && restoringRender.current) &&
    (design ? isReviewDesign(design) : !isLibraryEntry(state.entry));
  const caption = useShareCaption(state.playerId, state.reviewIds, captionWanted);
  const captionRef = React.useRef(caption);
  captionRef.current = caption;
  // A coach's post from a design with no prompt caption to start from (unpublished, or published
  // with the caption left blank) gets one written from the design's request as soon as it opens.
  const designCaptionWanted =
    isOpen &&
    !state.posted &&
    state.entry?.kind === "coach" &&
    !!design &&
    !isReviewDesign(design) &&
    !design.promptSettings?.message?.trim();
  // The request is on the detail only; an unpublished design's detail is already loaded (same key).
  const designDetail = useQuery({
    queryKey: designKeys.detail(design?.id ?? 0),
    queryFn: () => DesignPackageServices.get(design!.id),
    enabled: designCaptionWanted && design?.id !== unlistedDesignData?.id,
    staleTime: 15_000,
    retry: false,
  });
  // Without the detail, the design's name is enough to write from.
  const captionDesign =
    design && design.id === unlistedDesignData?.id
      ? unlistedDesignData
      : designDetail.data ??
        (designDetail.isError && design ? { id: design.id, name: design.name, instructions: "" } : undefined);
  const designCaptionState = useDesignCaption(captionDesign, state.playerId, designCaptionWanted);
  const designCaption = designCaptionWanted ? designCaptionState : null;

  // ---- transitions ----

  const close = React.useCallback(() => {
    setState(s => closedState(s.playerId, s.playerUserId));
    setResolving(false);
    setRenderStartFailed(false);
    lastRequestKey.current = "";
  }, []);

  const openFlow = React.useCallback((entry: ShareEntry) => {
    const pinned = pinnedIdOf(entry);
    setState(s => ({
      ...closedState(entry.kind === "coach" ? entry.playerId : s.playerId, s.playerUserId),
      ...(entry.kind === "coach" ? { playerUserId: entry.playerUserId ?? null } : {}),
      phase: "open",
      entry,
      designPackageId: entryDesignId(entry),
      reviewIds: pinned != null ? [pinned] : [],
    }));
    restoringRender.current = false;
    lastRequestKey.current = "";
    setRenderStartFailed(false);
    setResolving(true);
  }, []);

  // Changing what the post shows drops the current render; a new one starts for the new selection.
  const update = (patch: (s: ShareFlowState) => Partial<ShareFlowState>) =>
    setState(s => {
      const next = { ...s, ...patch(s) };
      return sameSelection(s, next) ? next : { ...next, renderGroupId: null };
    });

  const selectDesign = (id: number) =>
    update(s => ({
      designPackageId: id,
      singleDesignId: s.mode === "single" ? id : s.singleDesignId,
      backgroundAssetId: null,
      backgroundImageUrl: null,
    }));

  const selectLeadReview = (id: number) =>
    update(s => ({ reviewIds: [id, ...s.reviewIds.slice(1).filter(r => r !== id)] }));

  const startCarousel = () =>
    update(s => {
      const current = designById(s.designPackageId);
      const target = current && isCarouselLook(current) ? current : carouselLooks[0];
      if (!target) return {};
      const lead = s.reviewIds[0];
      const count = Math.min(CAROUSEL_DEFAULT_REVIEWS, target.maxReviews) - (lead != null ? 1 : 0);
      const others = pickBestReviews(postableReviews, count, lead != null ? [lead] : []);
      return {
        mode: "carousel",
        singleDesignId: s.designPackageId,
        designPackageId: target.id,
        reviewIds: lead != null ? [lead, ...others] : others,
        backgroundAssetId: null,
        backgroundImageUrl: null,
      };
    });

  const endCarousel = () =>
    update(s => ({
      mode: "single",
      designPackageId: s.singleDesignId ?? singleLooksOf(allDesigns)[0]?.id ?? null,
      reviewIds: s.reviewIds.slice(0, 1),
      backgroundAssetId: null,
      backgroundImageUrl: null,
    }));

  const setReviewIds = (ids: number[]) => update(() => ({ reviewIds: ids }));

  const addBestReviews = () =>
    update(s => {
      const d = designById(s.designPackageId);
      if (!d) return {};
      const picked = pickBestReviews(postableReviews, d.maxReviews - s.reviewIds.length, s.reviewIds);
      return { reviewIds: [...s.reviewIds, ...picked] };
    });

  const selectTheme = (themeId: number) => update(() => ({ themeId }));

  const selectBackground = (choice: BackgroundChoice) =>
    update(() => {
      if (choice === null) return { backgroundAssetId: null, backgroundImageUrl: null };
      if ("assetId" in choice) return { backgroundAssetId: choice.assetId, backgroundImageUrl: null };
      return { backgroundAssetId: null, backgroundImageUrl: choice.url };
    });

  const retryRender = () => {
    setState(s => ({ ...s, renderGroupId: null }));
    setRetryNonce(n => n + 1);
  };

  const onPosted = (post: CreatedPost | null) => {
    const s = stateRef.current;
    setState(current => ({
      ...current,
      posted: true,
      postedPostId: post?.postId != null ? Number(post.postId) : null,
    }));
    s.reviewIds.forEach(id => {
      queryClient.invalidateQueries({ queryKey: designKeys.reviewPosts(id) });
      queryClient.invalidateQueries({ queryKey: ["review-details", String(id)] });
    });
    queryClient.invalidateQueries({ queryKey: ["reviews-inbox-list-infinite"] });
    queryClient.invalidateQueries({ queryKey: ["reviews-unreplied-count"] });
    queryClient.invalidateQueries({ queryKey: ["calendarEvents"] });
    if (s.playerId != null) queryClient.invalidateQueries({ queryKey: designKeys.postableReviews(s.playerId) });
    onPostedProp?.(post);
  };

  const openPost = (postId: number, socialNetworkId?: SocialNetworksId) => setOpenedPost({ postId, socialNetworkId });
  const closePost = () => setOpenedPost(null);

  // ---- picking the look and reviews once designs (and a restored render) are known ----

  React.useEffect(() => {
    if (!resolving || !isOpen) return;
    if (designsQuery.isLoading || (!designsQuery.isFetched && designsQuery.fetchStatus !== "idle")) return;
    if (isApiError(designsQuery.error, "forbidden")) {
      toast.error(TOAST_NO_ACCESS);
      close();
      return;
    }
    const s = stateRef.current;

    // The coach's design isn't in the list: wait for it to load on its own, or give up if it's gone.
    if (s.entry?.kind === "coach" && s.designPackageId != null && s.designPackageId === coachDesignId) {
      if (!coachDesignListed && !unlistedDesign.data) {
        if (!unlistedDesign.isError) return;
        toast.error(TOAST_COACH_DESIGN_GONE);
        close();
        return;
      }
    }

    if (restoringRender.current && s.renderGroupId != null) {
      const group = render.group;
      if (!group && !render.notFound && !render.error) return;
      restoringRender.current = false;
      const groupDesign = group ? designById(group.designPackageId) : undefined;
      if (!group || render.notFound || Number(group.playerId) !== Number(s.playerId)) {
        toast.error(TOAST_RENDER_GONE);
        setState({ ...s, renderGroupId: null });
        return;
      }
      // The render's design may still be on its way in from the detail fetch
      if (!groupDesign && unlistedPending) return;
      if (!groupDesign) {
        toast.error(designGoneToast);
        setState({ ...s, renderGroupId: null, designPackageId: null });
        return;
      }
      const restored: ShareFlowState = {
        ...s,
        designPackageId: group.designPackageId,
        singleDesignId: s.mode === "single" ? group.designPackageId : s.singleDesignId,
        reviewIds: group.reviewIds,
        themeId: group.contentPageThemeId,
        backgroundAssetId: group.backgroundAssetId,
        backgroundImageUrl: group.backgroundImageUrl,
      };
      lastRequestKey.current = requestKey(requestFor(restored, groupDesign));
      // A restored caption counts as already written for these reviews.
      if (group.caption && group.captionStatus === "ready" && s.playerId != null) {
        const key = shareCaptionKey(s.playerId, group.reviewIds);
        if (queryClient.getQueryData(key) == null) queryClient.setQueryData(key, group.caption);
      }
      setState(restored);
      setResolving(false);
      return;
    }

    const current = designById(s.designPackageId);
    if (s.designPackageId != null && !current && unlistedPending) return;
    if (s.designPackageId != null && !current) {
      toast.error(designGoneToast);
      if (isLibraryEntry(s.entry)) {
        close();
        return;
      }
      setState({ ...s, designPackageId: null });
      return;
    }

    if (s.designPackageId == null) {
      const pool = s.mode === "carousel" ? allDesigns.filter(isCarouselLook) : singleLooksOf(allDesigns);
      const first = pool[0];
      setState({ ...s, designPackageId: first?.id ?? null, singleDesignId: first?.id ?? null });
      setResolving(false);
      return;
    }

    // A library review design starts from the player's best reviews, as many as it needs; one that needs
    // several opens as a carousel, with the first single look to fall back to.
    if (isReviewDesign(current) && s.reviewIds.length === 0) {
      if (postable.isLoading) return;
      const needed = Math.max(1, current!.minReviews);
      const best = pickBestReviews(postableReviews, needed, []);
      const carousel = needed > 1;
      setState({
        ...s,
        reviewIds: best,
        mode: carousel ? "carousel" : "single",
        singleDesignId: carousel ? singleLooksOf(allDesigns)[0]?.id ?? null : s.designPackageId,
      });
      setResolving(false);
      return;
    }

    setState({ ...s, singleDesignId: s.singleDesignId ?? s.designPackageId });
    setResolving(false);
  }, [
    resolving,
    isOpen,
    designsQuery.isLoading,
    designsQuery.isFetched,
    designsQuery.fetchStatus,
    designsQuery.error,
    allDesigns,
    unlistedDesign.data,
    unlistedDesign.isError,
    unlistedPending,
    render.group,
    render.notFound,
    render.error,
    postable.isLoading,
    postableReviews,
  ]);

  // ---- rendering whatever is selected ----

  const desired = isOpen && !resolving && !state.posted && design ? requestFor(state, design) : null;
  const desiredKey = requestKey(desired);

  const handleRenderError = (error: unknown, from: ShareFlowState, d: DesignPackageSummary) => {
    if (isApiError(error, "forbidden")) {
      toast.error(TOAST_NO_ACCESS);
      close();
      return;
    }
    if (isApiError(error, "review_not_postable")) {
      toast.error(TOAST_REVIEWS_NOT_SHAREABLE);
      if (from.playerId != null) queryClient.invalidateQueries({ queryKey: designKeys.postableReviews(from.playerId) });
      const offenders = ((error.details?.reviewIds as unknown[]) ?? []).map(Number);
      const pinned = pinnedIdOf(from.entry);
      if (pinned != null && offenders.includes(pinned)) {
        close();
        return;
      }
      const reviewIds = from.reviewIds.filter(id => !offenders.includes(id));
      // A carousel left with too few reviews falls back to the single post.
      if (from.mode === "carousel" && reviewIds.length < Math.max(2, d.minReviews)) {
        setState({
          ...from,
          mode: "single",
          designPackageId: from.singleDesignId ?? from.designPackageId,
          reviewIds: reviewIds.slice(0, 1),
          renderGroupId: null,
        });
        return;
      }
      setState({ ...from, reviewIds, renderGroupId: null });
      return;
    }
    if (isApiError(error, "not_found")) {
      toast.error(from.entry?.kind === "coach" ? TOAST_COACH_DESIGN_GONE : TOAST_DESIGN_GONE);
      queryClient.invalidateQueries({ queryKey: designKeys.availableDesigns(from.playerId ?? -1) });
      if (from.designPackageId != null) setGoneDesignIds(ids => [...ids, from.designPackageId as number]);
      setState({ ...from, designPackageId: null, renderGroupId: null });
      setResolving(true);
      return;
    }
    if (isApiError(error, "validation_error")) {
      const fields = ((error.details as { fields?: Record<string, unknown> } | undefined)?.fields ?? {}) as Record<
        string,
        unknown
      >;
      if (fields.backgroundImageUrl && from.backgroundImageUrl) {
        toast.error(TOAST_BG_UPLOAD_FAILED);
        setState({ ...from, backgroundImageUrl: null, renderGroupId: null });
        return;
      }
      if (fields.backgroundAssetId && from.backgroundAssetId != null) {
        toast.error(TOAST_BG_UNAVAILABLE);
        setState({ ...from, backgroundAssetId: null, renderGroupId: null });
        return;
      }
    }
    toast.error(TOAST_RENDER_FAILED);
    setRenderStartFailed(true);
  };

  const handledRetry = React.useRef(0);
  const requestInFlight = React.useRef(false);
  const failedKey = React.useRef<string>("");
  React.useEffect(() => {
    if (!desired || !design) return;
    const force = retryNonce !== handledRetry.current;
    if (!force && desiredKey === lastRequestKey.current) {
      // Same selection: nothing to do while its render exists, is on its way, or failed (Try again retries).
      if (state.renderGroupId != null || requestInFlight.current || failedKey.current === desiredKey) return;
    }
    const timer = setTimeout(() => {
      handledRetry.current = retryNonce;
      lastRequestKey.current = desiredKey;
      failedKey.current = "";
      requestInFlight.current = true;
      setRenderStartFailed(false);
      // Sent only when it's already written: the render never waits on the caption.
      const written =
        isReviewDesign(design) && captionRef.current.status === "ready"
          ? captionRef.current.caption ?? undefined
          : undefined;
      const from = stateRef.current;
      startShare.mutate(
        { ...desired, ...(written ? { caption: written } : {}), ...(force ? { force: true } : {}) },
        {
          onSuccess: group => {
            // A selection change since the request makes this result stale.
            if (lastRequestKey.current !== desiredKey || stateRef.current.phase === "closed") return;
            requestInFlight.current = false;
            setState(s => ({ ...s, renderGroupId: group.id }));
          },
          onError: error => {
            if (lastRequestKey.current !== desiredKey) return;
            requestInFlight.current = false;
            failedKey.current = desiredKey;
            handleRenderError(error, from, design);
          },
        }
      );
    }, RENDER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [desiredKey, retryNonce, state.renderGroupId]);

  const group = render.group;

  // Remember the theme the server chose for the player's first render; it's the one marked as theirs.
  React.useEffect(() => {
    if (!group || defaultThemeId != null || state.themeId != null) return;
    if (group.contentPageThemeId != null) setDefaultThemeId(group.contentPageThemeId);
  }, [group, state.themeId, defaultThemeId]);

  // ---- URL sync ----

  React.useEffect(() => {
    if (resolving && restoringRender.current) return;
    // A posted share is finished: a reload must not reopen it and post twice.
    if (state.phase === "closed" || state.posted) {
      if (parseShareParams(history.location.search)) writeShareParams(history, location, null);
      return;
    }
    // A coach's share can't be restored from the URL, and its keys would clobber the screen's own
    // (the editor keeps ?theme= for its preview), so it stays out of the URL.
    if (state.entry?.kind === "coach") return;
    writeShareParams(history, location, urlParamsFor(state));
  }, [state, resolving]);

  const value: ShareFlowValue = {
    ...state,
    open: openFlow,
    close,
    selectDesign,
    selectLeadReview,
    startCarousel,
    endCarousel,
    setReviewIds,
    addBestReviews,
    selectTheme,
    selectBackground,
    retryRender,
    onPosted,
    openPost,
    closePost,
    design,
    looks,
    carouselLooks,
    canCarousel,
    designsLoading: designsQuery.isLoading,
    designsHasMore: !!designsQuery.hasMore,
    designsLoadingMore: designsQuery.isLoadingMore,
    designsLoadMoreFailed: designsQuery.loadMoreFailed,
    loadMoreDesigns,
    pinnedId,
    pinnedReview,
    postableReviews,
    postableLoading: postable.isLoading,
    render,
    caption,
    designCaption,
    resolving,
    renderStarting: startShare.isPending,
    renderStartFailed,
    defaultThemeId,
    openedPost,
  };

  return <ShareFlowContext.Provider value={value}>{children}</ShareFlowContext.Provider>;
}
